import React, { useEffect, useState } from "react";
import { Row, Col, Button, Container, Badge } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getOnlineBookingSettings } from "../../acommsapi";
import { getUserPracticeById } from "../../api";
import AddEditOnlineBookings from "./AddEditOnlineBookings";

export function ViewOnlineBookings({ refresh, onOnlineBookingUpdate = () => {} }) {
  const [modalShow, setModalShow] = useState(false);
  const [practices, setPractices] = useState([]);
  const [onlineBookingData, setOnlineBookingData] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const loadBookingData = async () => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    const decoded = jwtDecode(token);
    const practiceNumbers = decoded.practice || [];

    const userPractices = await getUserPracticeById(decoded._id);
    setPractices(userPractices);

    const result = await getOnlineBookingSettings(practiceNumbers);
    setOnlineBookingData(Array.isArray(result.data) ? result.data : []);
  };

  useEffect(() => {
    loadBookingData();
  }, [refresh]);

  const handleButtonClick = () => {
    setSelectedBooking(onlineBookingData.length > 0 ? onlineBookingData[0] : null);
    setModalShow(true);
  };

  const mapToNames = (numbersArray) => {
    if (!Array.isArray(numbersArray)) return [];
    return numbersArray
      .map((num) => {
        const p = practices.find((pr) => pr.practiceNumber === num);
        return p ? p.practiceName : num;
      })
      .filter(Boolean);
  };

  const renderLogo = () => {
    const logoUrl = onlineBookingData[0]?.logo?.[0] || null;

    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt="Practice Logo"
          style={{
            maxHeight: "60px",
            maxWidth: "200px",
            objectFit: "contain",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "inline";
          }}
        />
      );
    } else {
      return <span className="text-muted">Using first practice logo</span>;
    }
  };

  return (
    <Container fluid className="pt-3 shadow-sm border">
      <Row className="mb-3">
        <Col>
        <h5 className="text-primary mb-3 ms-3">Online Booking Configuration</h5>
        </Col>
        <Col>
          <Button onClick={handleButtonClick} variant="primary" className="float-end">
            {onlineBookingData.length > 0 ? "Edit Online Booking Settings" : "Add Online Booking Settings"}
          </Button>
        </Col>
      </Row>

      {onlineBookingData.length > 0 && (
        <div className="px-3 mb-4">

          <Row className="mb-3">
            <Col md={6}>
              <strong>Booking Page Heading:</strong>
              <div className="mt-1">{onlineBookingData[0].bookingHeading || "Not set"}</div>
            </Col>
            <Col md={6}>
              <strong>Logo:</strong>
              <div className="mt-1">{renderLogo()}</div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <strong>Practices Enabled:</strong>
              <div className="mt-1">
                {mapToNames(onlineBookingData[0].selectedPractices || []).map((name, idx) => (
                  <Badge key={idx} bg="info" className="me-1 mb-1">
                    {name}
                  </Badge>
                ))}
                {(!onlineBookingData[0].selectedPractices || onlineBookingData[0].selectedPractices.length === 0) && (
                  <span className="text-muted">No practices selected</span>
                )}
              </div>
            </Col>
          </Row>

          {onlineBookingData[0].locations?.length > 0 && (
            <Row className="mb-3">
              <Col>
                <strong>Locations:</strong>
                <ul className="mb-0 mt-1">
                  {onlineBookingData[0].locations.map((loc, i) => (
                    <li key={i}>
                      <strong>{loc.heading}</strong>: {loc.address}
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={4}>
              <strong>Allow Over Padding:</strong>
              <div className="mt-1">
                <Badge bg={onlineBookingData[0].allowOverPadding ? "success" : "secondary"}>
                  {onlineBookingData[0].allowOverPadding ? "Yes" : "No"}
                </Badge>
              </div>
            </Col>
            <Col md={4}>
              <strong>Allow Overlap:</strong>
              <div className="mt-1">
                <Badge bg={onlineBookingData[0].allowOverlap ? "success" : "secondary"}>
                  {onlineBookingData[0].allowOverlap ? "Yes" : "No"}
                </Badge>
              </div>
            </Col>
            <Col md={4}>
              <strong>Overlap Minutes:</strong>
              <div className="mt-1">
                {onlineBookingData[0].allowOverlap && onlineBookingData[0].overlapMinutes
                  ? onlineBookingData[0].overlapMinutes
                  : "N/A"}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <strong>Service Catalog Types:</strong>
              <div className="mt-1">
                {onlineBookingData[0].serviceCatalog?.length > 0 ? (
                  onlineBookingData[0].serviceCatalog
                    .filter((s) => s && s.trim())
                    .map((s, idx) => (
                      <Badge key={idx} bg="outline-primary" text="primary" className="me-1 mb-1 border">
                        {s}
                      </Badge>
                    ))
                ) : (
                  <span className="text-muted">No service types configured</span>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <strong>Booking URL:</strong>
              <div className="mt-1">
                <p>{`https://practoplatform/#/onlinebooking/${onlineBookingData[0]._id}`}</p>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <AddEditOnlineBookings
        show={modalShow}
        onHide={() => setModalShow(false)}
        onlineBookingDetail={selectedBooking}
        onOnlineBookingUpdate={() => {
          setModalShow(false);
          loadBookingData();
          onOnlineBookingUpdate();
        }}
      />
    </Container>
  );
}
